import { CardSection } from "~/components/atoms/CardSection";
import { PlayerResult } from "~/lib/api/ecosystemV2.server";
import sortBy from "lodash/sortBy";
import * as format from "~/business/college/format";
import { cn } from "~/lib/utils";
import {
    Modal,
    ModalContent,
    ModalDescription,
    ModalHeader,
    ModalTitle,
    ModalTrigger,
} from "~/components/molecules/Modal";
import { Skeleton } from "~/components/atoms/Skeleton";

const cellStyles = "py-2 px-4 whitespace-nowrap sticky left-0";

function Th({ children, className }: { children: string; className?: string }) {
    return <th className={cn(cellStyles, className)}>{children}</th>;
}

function Td({ children, className }: { children: string; className?: string }) {
    return <td className={cn(cellStyles, className)}>{children}</td>;
}

function Table({ players }: { players: PlayerResult[] }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full table-auto text-left text-gray-80" aria-label="current roster">
                <thead>
                    <tr className="font-placeholder-title bg-accent-sky-blue">
                        <Th className="z-10 bg-accent-sky-blue">Name</Th>
                        <Th>Position</Th>
                        <Th>Height</Th>
                        <Th>Year</Th>
                    </tr>
                </thead>
                <tbody>
                    {players.map((p, i) => (
                        <tr
                            key={p.id}
                            className={cn(i % 2 && "bg-blue-10", "font-metadata-medium")}
                        >
                            <Td className={cn(i % 2 ? "bg-blue-10" : "bg-white", "z-10")}>
                                {p.attrs.name}
                            </Td>
                            <Td>{p.attrs.position}</Td>
                            <Td>{format.height(p.attrs.height_in_inches)}</Td>
                            <Td>{p.attrs.eligibility_year}</Td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const ProgramRosterContainer = ({
    children,
    title,
    testId,
}: { title: string; testId?: string } & React.PropsWithChildren) => (
    <CardSection title={title}>
        <div className="px-6" data-testid={testId}>
            {children}
        </div>
    </CardSection>
);

export const ProgramRosterLoading = () => (
    <ProgramRosterContainer title="Current Roster">
        <Skeleton className="h-[430px]" />
    </ProgramRosterContainer>
);

export const ProgramRosterError = () => (
    <ProgramRosterContainer title="Current Roster">
        <div className="text-center text-error-90">Error loading program roster information.</div>
    </ProgramRosterContainer>
);

export function ProgramRoster(props: { players?: PlayerResult[] }) {
    const players = sortBy(props.players, "attrs.name");
    if (players.length <= 0) return null;
    const title = "Current Roster";
    const testId = "program-roster-modal-content";
    const limit = 10;
    return (
        <Modal>
            <ProgramRosterContainer title={title} testId={testId}>
                <Table players={players.slice(0, limit)} />
                {players.length > limit && (
                    <ModalTrigger
                        className="font-metadata-medium px-4 py-2 text-blue-80"
                        data-testid={`show-more-button-${testId}`}
                        aria-controls={testId}
                    >
                        Show More
                    </ModalTrigger>
                )}
            </ProgramRosterContainer>
            <ModalContent className="size-full" data-testid={testId}>
                <ModalDescription>View Program Roster</ModalDescription>
                <ModalHeader>
                    <ModalTitle>{title}</ModalTitle>
                </ModalHeader>
                <Table players={players} />
            </ModalContent>
        </Modal>
    );
}
